import type { IToolkitConfigInterface } from '@gig/magic-frontend-toolkit/dist/services/config';
import type { Currency } from '@gig/magic-sdk/dist/adapter/users/signup';
import type { Country } from '@gig/magic-sdk/dist/adapter/users/getCountries';
import type {
    IMailCheckConfigDefinition,
    IPersonalIdConfig,
} from '@gig/magic-account-types';
import type { IPreferredVerificationMethods } from '@gig/magic-account/dist/util/verification-types';
import type { IRewardVideos } from '@gig/magic-gamification/dist/components/video-reward-modal';
import type { IMaxContactConfig } from '@gig/magic-messaging/dist/components/max-contact';
import type { IEmbeddedMessagingConfig } from '@gig/magic-messaging/dist/components/salesforce-messaging';

import type { IMiniLobbyConfig } from '@gig/magic-gameplay';

export enum PasswordValidationConditions {
    UPPERCASE = 'uppercase',
    LOWERCASE = 'lowercase',
    NUMBER = 'number',
    MINLENGTH = 'min-length',
    SYMBOL = 'symbol',
    MAXLENGTH = 'max-length',
    MINMAXLENGTH = 'min-max-length',
    NUMBERSONLY = 'numbers-only',
    DIFFERENT_DIGITS = 'different-digits',
    NO_DIGITS_SEQUENCE = 'no-digits-sequence',
}

/**
 * Value is intentionally left any, as we might need flexibility
 * in validation rules
 */
export interface IPasswordValidationAdvancedCondition {
    name: PasswordValidationConditions;
    value: any;
}

export interface IAppConfig extends IToolkitConfigInterface {
    cloudflareDomain: string;
    sessionExpireTime: number;

    gtmContainerId: string;

    gamePlay: {
        balanceDelay: number;
        depositLimitsRequiredCountries: Country[];
        playForFunBlockedCountries: Country[];
        playForFunBlockedCountriesWithInfo: Country[];
        favouritesEnabled: boolean;
        showInfoLink: boolean;
    };

    account: {
        showAccountClosureButton: boolean;
        idDocumentTypeWhitelist: {
            DEFAULT: number[];
            [country: string]: number[];
        };
    };

    countries: {
        lockedCountries: string[];
        childLockCountries: string[];
    };

    signup: {
        regilyEnabledCountries: {
            DEFAULT: boolean;
            [country: string]: boolean;
        };
        regilyUrl: string;
        legalAge: {
            DEFAULT: number;
            [country: string]: number;
        };
        maximumAge: {
            DEFAULT: number;
            [country: string]: number;
        };
        preferredVerificationMethod: IPreferredVerificationMethods;
        lockedCountries: string[];
        personalId: {
            DEFAULT: IPersonalIdConfig;
            [country: string]: IPersonalIdConfig;
        };
        iban: {
            DEFAULT: boolean;
            [country: string]: boolean;
        };
        phoneNumberValidator: {
            DEFAULT: string;
            [countryCallingCode: string]: string;
        };
        smsCodeLength: {
            DEFAULT: number;
            [country: string]: number;
        };
        passwordValidationConditions: {
            DEFAULT: Array<
                | PasswordValidationConditions
                | IPasswordValidationAdvancedCondition
            >;
        };
        phone: {
            DEFAULT: boolean;
            [country: string]: boolean;
        };
        validationMessagesEnabled: boolean;
        agcoOptionalFieldsAccordion: boolean;
        securityQuestions?: {
            enabled: {
                DEFAULT: boolean;
                [country: string]: boolean;
            };
            maxLength: {
                DEFAULT: number;
                [country: string]: number;
            };
        };
    };

    bankid: {
        defaultMin: Partial<Record<Currency, number>>;
        suggestedAmounts: Partial<Record<Currency, number[]>>;
        bonus: Partial<Record<Country, string>>;
        allowedOrigins: string[];
        provider: string;
    };

    homepage: {
        cmsPageSlug: string;
    };

    liveChat: {
        trackScriptUrl: string;
        id: string;
        chatId: string;
    };

    defaultGameCategories: string[];

    enableContinuePlaying?: boolean;

    showRewardValidityCountdown?: boolean;
    showProgressBarRewardButton?: boolean;

    lobbiesPath: string[];
    lobbiesRoutesToShowFavouriteGames?: string[];
    lobbiesRoutesToShowLastPlayedGames?: string[];

    allowEmailChange?: boolean;
    allowPhoneChange?: boolean;

    limits: {
        rgLimitsNotificationInterval?: number;
        rgLimitsNotificationEnabledCountries?: Country[];
        enabledTypes: {
            DEFAULT: number[];
            [country: string]: number[];
        };
        maxCap: {
            DEFAULT: number[][];
            [currency: string]: number[][];
        };
        periodsAllowed: {
            DEFAULT: Record<
                string,
                { DAILY: boolean; WEEKLY: boolean; MONTHLY: boolean }
            >;
            [country: string]: Record<
                string,
                { DAILY: boolean; WEEKLY: boolean; MONTHLY: boolean }
            >;
        };
    };

    bettingLimitsRequired: boolean;

    sow?: {
        enabled: boolean;
    };

    selfExcludeExternalAuthority: {
        DEFAULT: boolean;
        [country: string]: boolean;
    };

    selfExcludeEnabledCategories?: number[];

    leaderboards: {
        itemsPerPage: number;
    };

    cookieBot: {
        src: string;
        id: string;
        dataCbid: string;
        type: string;
        dataBlockingmode: string;
    };

    cookieService: {
        value: string;
    };

    carousel: {
        autoplay: boolean;
        duration: number;
    };

    zendesk?: {
        version: string;
        languages: string[];
        labels: {
            [language: string]: string;
        };
        defaultLanguage: string;
        sdkUrl: string;
    };

    LC?: {
        license: string;
        sdkUrl: string;
    };

    maxContact?: IMaxContactConfig;

    salesforceMessaging?: IEmbeddedMessagingConfig;

    isSeeAllHyperlink: boolean;
    gamesShownInSearch: number;
    gamesShownInProviders: number;
    mailCheck: IMailCheckConfigDefinition;
    seasonalRewards: IRewardVideos;
    wheelRewards: IRewardVideos;
    christmasRewards: IRewardVideos;
    emailClients: any;
    realityCheck?: {
        [jurisdiction: string]: {
            required: boolean;
            interval: number;
            duration: number;
            message: string;
        };
    };
    miniLobby?: IMiniLobbyConfig;
}
